/**
 * Dev game setup: a pair by seed, a daily by date, any valid pair, or a
 * restart. Games start straight in the gameplay store; a sandboxed one never
 * touches the saved game (see isSandboxed).
 */
import { ariadneSeat } from "../ariadne/seat";
import { seededRandom } from "../game/challengeSelector";
import { generatePair } from "../game/pairGenerator";
import { useGameplayStore } from "../game/stores/useGameplayStore";
import { dailyChallengesService } from "../services/DailyChallengesService";
import type { GraphData } from "../services/dataLoader";
import { unifiedDataStore } from "../services/UnifiedDataStore";
import { useAssetStore } from "../stores/useAssetStore";
import { useModalStore } from "../stores/useModalStore";
import { bestRoutePath } from "../utils/graphUtils";
import { isSandboxed, useDevSession } from "./devSession";

export const normalizeWord = (w: string) => w.trim().toLowerCase();

/** Why a pair can't be played, or null when it can. */
export function validatePair(
  graph: GraphData | null,
  start: string,
  target: string,
): string | null {
  if (!graph) return "The graph is not loaded yet.";
  const a = normalizeWord(start);
  const b = normalizeWord(target);
  if (!a || !b) return "Enter a start and a target word.";
  if (!graph[a]) return `"${a}" is not in the graph.`;
  if (!graph[b]) return `"${b}" is not in the graph.`;
  if (a === b) return "Start and target must differ.";
  if (bestRoutePath(graph, a, b).length < 2) {
    return `No path from "${a}" to "${b}".`;
  }
  return null;
}

/** The deterministic pair for a seed (same generator as random games). */
export function pairForSeed(
  graph: GraphData | null,
  seed: number,
): { start: string; target: string } | null {
  if (!graph) return null;
  const p = generatePair(graph, { rng: seededRandom(`dev-seed:${seed}`) });
  return p ? { start: p.start, target: p.end } : null;
}

export const isIsoDate = (d: string) =>
  /^\d{4}-\d{2}-\d{2}$/.test(d) && !Number.isNaN(Date.parse(`${d}T00:00:00Z`));

interface StartOpts {
  daily?: { id: string };
}

/** Start a game on (start, target). Returns an error message or null. */
export async function startDevPair(
  start: string,
  target: string,
  opts: StartOpts = {},
): Promise<string | null> {
  const graph = useAssetStore.getState().graphData;
  const err = validatePair(graph, start, target);
  if (err || !graph) return err;
  useDevSession.getState().markUsed();
  const a = normalizeWord(start);
  const b = normalizeWord(target);
  // The gold path, fixed at game start.
  const goldPath = bestRoutePath(graph, a, b);
  const modal = useModalStore.getState();
  modal.hideGameReportModal();
  modal.discardHeldGameReport();
  const store = useGameplayStore.getState();
  const state = {
    startWord: a,
    targetWord: b,
    currentWord: a,
    playerPath: [a],
    optimalPath: goldPath,
    suggestedPathFromCurrent: [...goldPath],
    gameStatus: "playing" as const,
    isChallenge: false,
    isDailyChallenge: !!opts.daily,
    isTutorial: false,
    currentDailyChallengeId: opts.daily?.id,
    aiPath: [],
    aiModel: null,
    ariadneStatus: null,
    gameReport: null,
    potentialRarestMovesThisGame: [],
  };
  ariadneSeat.abandon();
  store.resetGameState();
  useGameplayStore.getState().setGameState(state);
  if (!isSandboxed()) {
    await unifiedDataStore.saveCurrentGame(
      {
        ...state,
        pathDisplayMode: useGameplayStore.getState().pathDisplayMode,
        optimalChoices: [],
        backtrackHistory: [],
        startTime: Date.now(),
      },
      "regular",
    );
  }
  return null;
}

export async function startSeedGame(seed: number): Promise<string | null> {
  if (!Number.isFinite(seed)) return "Enter a number.";
  const pair = pairForSeed(useAssetStore.getState().graphData, seed);
  if (!pair) return "No pair for that seed.";
  return startDevPair(pair.start, pair.target);
}

export async function startDailyForDate(date: string): Promise<string | null> {
  if (!isIsoDate(date)) return "Use a date like 2026-10-06.";
  const c = dailyChallengesService.getChallengeForDate(date);
  if (!c) return `No daily for ${date}.`;
  return startDevPair(c.startWord, c.targetWord, { daily: { id: c.id } });
}

/** Restart the current pair (a daily stays a daily). */
export async function restartPair(): Promise<string | null> {
  const s = useGameplayStore.getState();
  if (!s.startWord || !s.targetWord) return "No game to restart.";
  return startDevPair(s.startWord, s.targetWord, {
    daily:
      s.isDailyChallenge && s.currentDailyChallengeId
        ? { id: s.currentDailyChallengeId }
        : undefined,
  });
}
